import { Smartphone, ClipboardList, Eye, ListChecks, AlertTriangle, BookOpen, Wrench, BarChart3, HelpCircle } from "lucide-react";
import { PageHero, Section, SectionTitle, CTABand } from "@/components/ui-blocks/Blocks";

const FEATURES = [
  { icon: ClipboardList, label: "Step-by-step pre-trip inspection guide" },
  { icon: Eye, label: "Exterior inspection walkthrough" },
  { icon: ListChecks, label: "Interior inspection walkthrough" },
  { icon: AlertTriangle, label: "Brake check review" },
  { icon: HelpCircle, label: "Emergency equipment review" },
  { icon: BookOpen, label: "Plain-language explanations" },
  { icon: Smartphone, label: "Visual learning support" },
  { icon: ListChecks, label: "Practice checklists" },
  { icon: AlertTriangle, label: "Common mistake reminders" },
  { icon: BookOpen, label: "Study sections" },
  { icon: Wrench, label: "Trainer support tools" },
  { icon: BarChart3, label: "Future quiz & progress tracking" },
];

const WHY = [
  "Confidence","Retention","Inspection order","Test readiness","Real-world safety habits",
];

export default function PretripGuide() {
  return (
    <>
      {/* Custom hero — bold "coming soon" */}
      <section data-testid="guide-hero" className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFCC00]/10 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#FFCC00] text-[#FFCC00] px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-semibold mb-6">
              <span className="h-1.5 w-1.5 bg-[#FFCC00] animate-pulse" /> Work in Progress · Coming Soon
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tight leading-[0.95]">
              CLB Solution<br />
              <span className="text-[#FFCC00]">Pretrip Guide</span>
            </h1>
            <p className="mt-6 text-white/75 max-w-xl text-base md:text-lg leading-relaxed">
              A mobile-friendly school bus pre-trip inspection training tool currently in
              development. Designed to help trainees study smarter, practice more effectively, and
              build confidence before test day and real-world bus operation.
            </p>
            <p className="mt-3 text-sm text-white/50">
              The CLB Solution Pretrip Guide will be announced when ready.
            </p>
          </div>

          {/* Mock phone */}
          <div className="lg:col-span-5">
            <div data-testid="guide-mockup" className="relative mx-auto w-[260px] md:w-[300px] aspect-[9/19] border-2 border-white/15 bg-[#050505] rounded-[36px] overflow-hidden shadow-2xl">
              <div className="h-6 bg-black flex items-center justify-center">
                <span className="h-1 w-16 bg-white/20 rounded-full" />
              </div>
              <div className="p-5 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">Pretrip Guide</div>
                <div className="font-display text-xl font-bold uppercase">Exterior · Step 4</div>
                <div className="text-xs text-white/60">Check lights and reflectors. Confirm color, operation, and lens condition.</div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {["Headlights","Markers","Reflectors","Stop arm"].map((s) => (
                    <div key={s} className="border border-white/10 bg-[#0c0c0e] p-2 text-[10px] uppercase tracking-wider text-white/80">{s}</div>
                  ))}
                </div>
                <div className="mt-4 h-2 bg-white/10 overflow-hidden">
                  <div className="h-full w-2/5 bg-[#FFCC00]" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 flex justify-between">
                  <span>04 / 22</span><span>40%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hazard-stripes h-3" />
      </section>

      <Section testid="guide-purpose">
        <SectionTitle eyebrow="App Purpose" title="Help trainees review pre-trip material outside of formal training." description="The app will provide structured support for learning the full inside and outside school bus inspection." />
      </Section>

      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="guide-features">
        <SectionTitle eyebrow="Planned Features" title="Designed for repetition, retention, and real-world readiness." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-[#09090b] p-6 flex items-start gap-4 hover:bg-[#18181b] transition-colors">
              <f.icon size={22} className="text-[#FFCC00] mt-1 shrink-0" strokeWidth={1.5} />
              <span className="text-sm text-white/85">{f.label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section testid="guide-why">
        <SectionTitle eyebrow="Why the App Matters" title="Trainees need repetition to master the pre-trip." />
        <div className="grid sm:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {WHY.map((w, i) => (
            <div key={w} className="bg-[#09090b] p-6">
              <div className="font-mono text-[10px] text-[#FFCC00] mb-2">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-xl font-bold uppercase">{w}</div>
            </div>
          ))}
        </div>
      </Section>

      <CTABand title="Want to be notified when the app launches?" description="Email CLB Solution to be the first to hear about the Pretrip Guide release." />
    </>
  );
}
